/*
 * Locale / region config for 513travel.com.
 *
 * Adapted from the Studio locale-config.js. Keeps the three concepts the
 * Studio app separated — app UI language, region, and currency — but drops the
 * Studio-specific Flutterwave hosted-checkout link registry (payment links now
 * live in paymentConfig.js as a pattern only).
 *
 * Detection is sync and never throws, so the boot is never blocked. Currency
 * follows the detected country; the app language follows: saved override >
 * supported browser language > country default > English.
 */

// App-UI languages we surface. Caption/output languages are a Studio concept
// and intentionally not carried over.
export const APP_LANGUAGES = ["en", "fr", "pt", "ar", "sw"];

const STORAGE_KEY_APP_LANGUAGE = "travel.appLanguage";

// Two-letter ISO 3166-1 alpha-2 -> { app language, currency }.
// Africa-forward, with a global fallback to USD/English.
export const COUNTRY_DEFAULTS = {
  NG: { app: "en", currency: "NGN" },
  GH: { app: "en", currency: "GHS" },
  KE: { app: "sw", currency: "KES" },
  TZ: { app: "sw", currency: "TZS" },
  RW: { app: "en", currency: "RWF" },
  UG: { app: "en", currency: "UGX" },
  ZA: { app: "en", currency: "ZAR" },
  ZM: { app: "en", currency: "ZMW" },
  MU: { app: "en", currency: "MUR" },
  MW: { app: "en", currency: "MWK" },
  GM: { app: "en", currency: "GMD" },
  SL: { app: "en", currency: "SLL" },
  BW: { app: "en", currency: "BWP" },
  // West African CFA franc (XOF)
  CI: { app: "fr", currency: "XOF" },
  SN: { app: "fr", currency: "XOF" },
  BJ: { app: "fr", currency: "XOF" },
  TG: { app: "fr", currency: "XOF" },
  BF: { app: "fr", currency: "XOF" },
  ML: { app: "fr", currency: "XOF" },
  NE: { app: "fr", currency: "XOF" },
  GW: { app: "pt", currency: "XOF" },
  // Central African CFA franc (XAF)
  CM: { app: "fr", currency: "XAF" },
  GA: { app: "fr", currency: "XAF" },
  CG: { app: "fr", currency: "XAF" },
  TD: { app: "fr", currency: "XAF" },
  CF: { app: "fr", currency: "XAF" },
  GQ: { app: "en", currency: "XAF" },
  // North Africa / Arabic
  EG: { app: "ar", currency: "EGP" },
  MA: { app: "ar", currency: "MAD" },
  DZ: { app: "ar", currency: "DZD" },
  TN: { app: "ar", currency: "TND" },
  // Lusophone
  AO: { app: "pt", currency: "AOA" },
  MZ: { app: "pt", currency: "MZN" },
  CV: { app: "pt", currency: "CVE" },
  // Common diaspora / global markets
  GB: { app: "en", currency: "GBP" },
  US: { app: "en", currency: "USD" },
  CA: { app: "en", currency: "CAD" },
  FR: { app: "fr", currency: "EUR" },
  PT: { app: "pt", currency: "EUR" },
  AE: { app: "ar", currency: "AED" },
};

export const FALLBACK_DEFAULTS = { app: "en", currency: "USD", country: "" };

export function normalizeCountry(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

export function resolveCountryDefaults(country) {
  const code = normalizeCountry(country);
  const found = code && COUNTRY_DEFAULTS[code];
  if (found) return { ...found, country: code };
  return { ...FALLBACK_DEFAULTS };
}

// --- detection hints (all sync, all best-effort) ---

export function browserLanguage() {
  if (typeof navigator === "undefined") return "";
  const tag = (navigator.language || navigator.languages?.[0] || "").toLowerCase();
  const base = tag.split("-")[0];
  return base || "";
}

function browserCountryHint() {
  if (typeof navigator === "undefined") return "";
  const tag = navigator.language || navigator.languages?.[0] || "";
  const parts = tag.split("-");
  return parts.length > 1 ? normalizeCountry(parts[1]) : "";
}

function timezoneCountryHint() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    // Tiny map of common African + diaspora zones -> country. Best-effort only.
    const map = {
      "Africa/Lagos": "NG",
      "Africa/Accra": "GH",
      "Africa/Nairobi": "KE",
      "Africa/Dar_es_Salaam": "TZ",
      "Africa/Johannesburg": "ZA",
      "Africa/Kigali": "RW",
      "Africa/Kampala": "UG",
      "Africa/Cairo": "EG",
      "Africa/Casablanca": "MA",
      "Africa/Luanda": "AO",
      "Europe/London": "GB",
      "Europe/Paris": "FR",
      "Europe/Lisbon": "PT",
    };
    return map[tz] || "";
  } catch {
    return "";
  }
}

// Detected visitor country: backend-injected value wins (later task), then
// browser region tag, then timezone. Returns "" when unknown.
export function detectVisitorCountry() {
  const injected =
    typeof window !== "undefined" ? normalizeCountry(window.__VISITOR_COUNTRY__) : "";
  return injected || browserCountryHint() || timezoneCountryHint() || "";
}

// --- app language persistence + resolution ---

export function getStoredAppLanguage() {
  try {
    const v = localStorage.getItem(STORAGE_KEY_APP_LANGUAGE);
    return v && APP_LANGUAGES.includes(v) ? v : "";
  } catch {
    return "";
  }
}

export function setStoredAppLanguage(lang) {
  try {
    if (APP_LANGUAGES.includes(lang)) {
      localStorage.setItem(STORAGE_KEY_APP_LANGUAGE, lang);
    }
  } catch {
    /* ignore */
  }
}

// saved override > supported browser language > country default > English.
export function getAppLanguage(country) {
  const manual = getStoredAppLanguage();
  if (manual) return manual;
  const lang = browserLanguage();
  if (lang && APP_LANGUAGES.includes(lang)) return lang;
  return resolveCountryDefaults(country).app;
}

// Convenience: resolve the full locale picture in one call.
export function resolveLocale() {
  const country = detectVisitorCountry();
  const defaults = resolveCountryDefaults(country);
  return {
    country: defaults.country,
    currency: defaults.currency,
    language: getAppLanguage(country),
  };
}
